import { useState } from "react";
import { Layout } from "@/components/layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { TaskList } from "@/components/ui/task-list";
import { useTasks, type Task } from "@/hooks/use-tasks";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar as CalendarIcon, Pin, CheckCircle, AlertCircle, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { format, parseISO, isValid } from "date-fns";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { SimpleCalendar } from "@/components/task-calendar/simple-calendar";

interface TaskCalendarViewProps {
  tasks: Task[] | undefined;
  onSelectDate: (dateStr: string, tasks: Task[]) => void;
}

// Calendar view component for tasks
function TaskCalendarView({ tasks, onSelectDate }: TaskCalendarViewProps) {
  return (
    <SimpleCalendar 
      tasks={tasks} 
      onDateSelect={onSelectDate} 
    />
  );
}

export default function TasksPage() {
  const [selectedTab, setSelectedTab] = useState<string>("pending");
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [priorityFilter, setPriorityFilter] = useState<string>("");
  const [sortBy, setSortBy] = useState<string>("dueDate");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [calendarOpen, setCalendarOpen] = useState<boolean>(false);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedDateTasks, setSelectedDateTasks] = useState<Task[]>([]);
  
  // Get tasks based on selected tab
  const { 
    tasks, 
    createTask, 
    updateTask, 
    toggleTask, 
    deleteTask,
    isLoading,
    isPendingCreate,
    isPendingUpdate,
    isPendingToggle,
    isPendingDelete
  } = useTasks(); // Remove status filter - we'll filter on frontend
  
  // Debug: Log the tasks data
  console.log("Tasks data in component:", tasks);
  console.log("Selected tab:", selectedTab);
  console.log("Is tasks array?", Array.isArray(tasks));
  
  // Filter and sort tasks
  const filteredTasks = Array.isArray(tasks) ? tasks.filter((task) => {
      // First filter by tab status
      if (selectedTab === "pending" && task.status !== "pending") {
        return false;
      }
      if (selectedTab === "completed" && task.status !== "completed") {
        return false;
      }
      if (selectedTab === "overdue") {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (task.status !== "pending" || !task.dueDate || new Date(task.dueDate) >= today) {
          return false;
        }
      }
      
      // Filter by search term
      if (searchTerm && !task.title.toLowerCase().includes(searchTerm.toLowerCase())) {
        return false;
      }
      
      // Filter by priority
      if (priorityFilter && priorityFilter !== "all" && task.priority !== priorityFilter) {
        return false;
      }
      
      return true;
    })
    .sort((a, b) => {
      // Sort by selected field
      if (sortBy === "dueDate") {
        // Sort by due date (handle null/undefined dates)
        if (!a.dueDate) return sortOrder === "asc" ? 1 : -1;
        if (!b.dueDate) return sortOrder === "asc" ? -1 : 1;
        
        return sortOrder === "asc"
          ? new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime()
          : new Date(b.dueDate).getTime() - new Date(a.dueDate).getTime();
      }
      
      if (sortBy === "priority") {
        // Sort by priority (high > medium > low)
        const priorityValues = { high: 3, medium: 2, low: 1 };
        const aValue = priorityValues[a.priority as keyof typeof priorityValues];
        const bValue = priorityValues[b.priority as keyof typeof priorityValues];
        
        return sortOrder === "asc" ? aValue - bValue : bValue - aValue;
      }
      
      // Sort by title
      return sortOrder === "asc"
        ? a.title.localeCompare(b.title)
        : b.title.localeCompare(a.title);
    }) : [];
  
  // Get today's date for due date comparison
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  // Calculate counts for task categories
  const pendingCount = Array.isArray(tasks) ? tasks.filter(task => task.status === "pending").length : 0;
  const completedCount = Array.isArray(tasks) ? tasks.filter(task => task.status === "completed").length : 0;
  const overdueCount = Array.isArray(tasks) ? tasks.filter(task => 
    task.status === "pending" && 
    task.dueDate && 
    new Date(task.dueDate) < today
  ).length : 0;

  return (
    <Layout>
      <div className="px-4 py-6 sm:px-6 lg:px-8">
        {/* Page header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-neutral-900">Tasks</h1>
            <p className="mt-1 text-sm text-neutral-500">
              Manage and track your bookkeeping tasks
            </p>
          </div>
          <div className="mt-4 md:mt-0">
            <Dialog open={calendarOpen} onOpenChange={setCalendarOpen}>
              <DialogTrigger asChild>
                <Button>
                  <CalendarIcon className="h-4 w-4 mr-2" />
                  View Calendar
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[650px] max-h-[90vh] overflow-auto">
                <DialogHeader>
                  <DialogTitle className="flex items-center">
                    <CalendarIcon className="h-5 w-5 mr-2" />
                    Task Calendar
                  </DialogTitle>
                </DialogHeader>
                <div className="mt-4 space-y-6">
                  <div className="p-4 border rounded-lg bg-white shadow">
                    <TaskCalendarView 
                      tasks={tasks} 
                      onSelectDate={(dateStr, dateTasks) => {
                        setSelectedDate(dateStr);
                        setSelectedDateTasks(dateTasks);
                      }}
                    />
                  </div>
                  
                  {selectedDate && (
                    <div className="mt-4">
                      <h3 className="text-lg font-medium mb-2">
                        Tasks for {selectedDate && format(parseISO(selectedDate), 'MMMM d, yyyy')}
                      </h3>
                      {selectedDateTasks.length > 0 ? (
                        <div className="space-y-2">
                          {selectedDateTasks.map((task: Task) => (
                            <div key={task.id} className="p-3 border rounded-md">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center">
                                  <div className={`h-2 w-2 rounded-full mr-2 ${
                                    task.priority === 'high' ? 'bg-red-500' : 
                                    task.priority === 'medium' ? 'bg-yellow-500' : 'bg-green-500'
                                  }`} />
                                  <span className="font-medium">{task.title}</span>
                                </div>
                                <Badge>{task.priority || 'low'}</Badge>
                              </div>
                              {task.description && (
                                <p className="text-sm text-neutral-500 mt-1">{task.description}</p>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-neutral-500">No tasks scheduled for this date.</p>
                      )}
                    </div>
                  )}
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>
        
        {/* Task Filters */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="search">Search Tasks</Label>
                <Input
                  id="search"
                  placeholder="Search by title..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="priority">Filter by Priority</Label>
                <Select value={priorityFilter} onValueChange={setPriorityFilter}>
                  <SelectTrigger id="priority">
                    <SelectValue placeholder="All Priorities" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Priorities</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="low">Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="sort">Sort By</Label>
                <div className="flex gap-2">
                  <Select value={sortBy} onValueChange={setSortBy}>
                    <SelectTrigger id="sort" className="flex-1">
                      <SelectValue placeholder="Sort By" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="dueDate">Due Date</SelectItem>
                      <SelectItem value="priority">Priority</SelectItem>
                      <SelectItem value="title">Title</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setSortOrder(sortOrder === "asc" ? "desc" : "asc")}
                  >
                    {sortOrder === "asc" ? "↑" : "↓"}
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* Task Tabs */}
        <Tabs value={selectedTab} onValueChange={setSelectedTab} className="space-y-6">
          <TabsList className="grid grid-cols-3 w-full md:w-auto">
            <TabsTrigger value="pending" className="flex items-center gap-2">
              <Pin className="h-4 w-4" />
              <span>Pending</span>
              {pendingCount > 0 && (
                <span className="bg-primary/10 text-primary text-xs px-2 py-0.5 rounded-full">
                  {pendingCount}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="completed" className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4" />
              <span>Completed</span>
              {completedCount > 0 && (
                <span className="bg-primary/10 text-primary text-xs px-2 py-0.5 rounded-full">
                  {completedCount}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="overdue" className="flex items-center gap-2">
              <AlertCircle className="h-4 w-4" />
              <span>Overdue</span>
              {overdueCount > 0 && (
                <span className="bg-red-100 text-red-800 text-xs px-2 py-0.5 rounded-full">
                  {overdueCount}
                </span>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Task Lists for each tab */}
          <TabsContent value="pending">
            <Card>
              <CardHeader className="px-6 py-5 border-b">
                <CardTitle className="text-lg">Pending Tasks</CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <TaskList
                  tasks={filteredTasks}
                  onComplete={(id) => { void toggleTask(id); }}
                  onAdd={createTask}
                  onEdit={(id, task) => { void updateTask({ id, ...task }); }}
                  onDelete={(id) => { void deleteTask(id); }}
                  isLoading={isLoading}
                  isPendingAdd={isPendingCreate}
                  isPendingEdit={isPendingUpdate}
                  isPendingComplete={isPendingToggle}
                  isPendingDelete={isPendingDelete}
                />
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="completed">
            <Card>
              <CardHeader className="px-6 py-5 border-b">
                <CardTitle className="text-lg">Completed Tasks</CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <TaskList
                  tasks={filteredTasks}
                  onComplete={(id) => { void toggleTask(id); }}
                  onEdit={(id, task) => { void updateTask({ id, ...task }); }}
                  onDelete={(id) => { void deleteTask(id); }}
                  isLoading={isLoading}
                  isPendingEdit={isPendingUpdate}
                  isPendingComplete={isPendingToggle}
                  isPendingDelete={isPendingDelete}
                  showAddButton={false}
                />
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="overdue">
            <Card>
              <CardHeader className="px-6 py-5 border-b">
                <CardTitle className="text-lg">Overdue Tasks</CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <TaskList
                  tasks={filteredTasks}
                  onComplete={(id) => { void toggleTask(id); }}
                  onEdit={(id, task) => { void updateTask({ id, ...task }); }}
                  onDelete={(id) => { void deleteTask(id); }}
                  isLoading={isLoading}
                  isPendingEdit={isPendingUpdate}
                  isPendingComplete={isPendingToggle}
                  isPendingDelete={isPendingDelete}
                  showAddButton={false}
                />
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
